import React, {useEffect, useState} from 'react';
import {Modal, Button} from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { PedidoModification } from '../../../../Types';
import TableDateFormatter from '../../../../../../../../components/common/Table/TableDateFormatter';
import { formatCurrency } from '../../../../../../../../utils/functions';
import { getModificationReasons } from '../../../../../../../../crud/pedidos_api/pedidos_modifications';
import './ModificationDetail.scss';

interface ModificationDetailProps {
  modification: PedidoModification | null;
  onClose: () => void;
  show: boolean;
}

const ModificationDetail: React.FC<ModificationDetailProps> = ({modification, onClose, show}) => {
  const [selectedModification, setSelectedModification] = useState<PedidoModification | null>(modification);
  const [modificationReasons, setModificationReasons] = useState<Record<string, string>>({});

  useEffect(() => {
    setSelectedModification(modification);
  }, [modification]);

  // Fetch modification reasons when modal opens
  useEffect(() => {
    if (show && Object.keys(modificationReasons).length === 0) {
      getModificationReasons()
        .then((response) => {
          setModificationReasons(response.data);
        })
        .catch((error) => {
          console.error('Error fetching modification reasons:', error);
        });
    }
  }, [show, modificationReasons]);

  if (!selectedModification) {
    return null;
  }

  // Extract relevant metadata info
  const getItemInfo = () => {
    const metadata = selectedModification.modification_metadata;
    if (!metadata) return null;

    const before = metadata.before;
    const after = metadata.after;

    if (!before) return null;

    return {
      itemName: before.item_name || 'N/A',
      quantity: before.quantity || 0,
      basePrice: before.base_price || 0,
      totalPrice: before.total_price || 0,
      comment: before.comment || null,
      selectionGroups: before.selection_groups || [],
      after: after
    };
  };

  // Extract comment from modification_metadata at the root level
  const getRecordComment = () => {
    const metadata = selectedModification.modification_metadata;
    if (!metadata) return null;
    return metadata.comment || null;
  };

  const itemInfo = getItemInfo();
  const recordComment = getRecordComment();

  // Get the modification reason text
  const getModificationReasonText = () => {
    if (!selectedModification.modification_reason) return null;
    const reasonKey = String(selectedModification.modification_reason);
    return modificationReasons[reasonKey] || null;
  };

  const reasonText = getModificationReasonText();

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Detalle de la modificación</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '1rem' }}>
        <div className="mod-detail-body">
          {/* Main info grid - compact 2 columns */}
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Usuario</span>
              <span className="info-value">{selectedModification.user_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Ejecutor</span>
              <span className="info-value">{selectedModification.approver_user_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Mesa</span>
              <span className="info-value">
                {selectedModification.mesa_tag || selectedModification.mesa_number || selectedModification.mesa_id || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha</span>
              <span className="info-value">
                <TableDateFormatter 
                  data={selectedModification.timestamp} 
                  isRealTime={false} 
                  stopCounting={true} 
                  isTimestamp={true} 
                />
              </span>
            </div>
          </div>

          {/* Amount display - inline */}
          <div className="amount-row">
            <span className="amount-label">Monto</span>
            <span className={`amount-value ${selectedModification.isnegative ? 'negative' : 'positive'}`}>
              {selectedModification.isnegative ? '↓' : '↑'} {formatCurrency(Math.abs(selectedModification.modification_amount || 0))}
            </span>
          </div>

          {/* Description */}
          {selectedModification.modification_detail && (
            <div className="detail-row">
              <span className="detail-label">Detalle</span>
              <p className="detail-text">{selectedModification.modification_detail}</p>
            </div>
          )}

          {/* Modification Reason - only if exists */}
          {reasonText && (
            <div className="reason-section">
              <span className="reason-label">Razón de Modificación</span>
              <div className="reason-badge">
                <span className="reason-icon">⚠️</span>
                <span className="reason-text">{reasonText}</span>
              </div>
            </div>
          )}

          {/* Record Comment - only if exists */}
          {recordComment && (
            <div className="comment-section">
              <span className="comment-label">Comentario del Pedido</span>
              <div className="comment-text">
                <span className="comment-icon">💬</span>
                <p>{recordComment}</p>
              </div>
            </div>
          )}

          {/* Item info - only if exists */}
          {itemInfo && (
            <div className="item-info">
              <div className="item-row">
                <span className="item-label">Producto</span>
                <span className="item-value">{itemInfo.itemName}</span>
                <span className="item-qty">x{itemInfo.quantity}</span>
              </div>
              
              {itemInfo.totalPrice > 0 && (
                <div className="item-row">
                  <span className="item-label">Precio</span>
                  <span className="item-value">{formatCurrency(itemInfo.totalPrice)}</span>
                </div>
              )}

              {itemInfo.comment && (
                <div className="item-row">
                  <span className="item-label">Comentario</span>
                  <span className="item-value">{itemInfo.comment}</span>
                </div>
              )}

              {itemInfo.selectionGroups.length > 0 && (
                <div className="selections">
                  {itemInfo.selectionGroups.map((group: any, idx: number) => (
                    group.selected_items && group.selected_items.length > 0 && (
                      <div key={idx} className="selection-list">
                        {group.selected_items.map((item: any, itemIdx: number) => (
                          <span key={itemIdx} className="selection-tag">
                            {item.item_name}
                            {item.quantity > 1 && ` x${item.quantity}`}
                          </span>
                        ))}
                      </div>
                    )
                  ))}
                </div>
              )}

              {itemInfo.after && (
                <div className="change-note">
                  Cantidad: {itemInfo.after.old_quantity || 0} → {itemInfo.after.new_quantity || 0}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <div className='form-modal-footer-custom'>
          <Button variant="none" className="form-button-cancel" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ModificationDetail;

